import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Edit3, Flame, Gift, Plus, RotateCw, Save, Trash2, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { VerifiedName } from "../../components/VerifiedBadge";

const defaultSettings = {
  achievements_enabled: true,
  achievements_locked_message: "",
  leaderboard_rewards_enabled: true,
  leaderboard_locked_message: "",
  daily_spin_enabled: true,
  daily_spin_locked_message: "",
  play_streak_enabled: true,
  play_streak_locked_message: "",
};

const defaultDailySpinRewards = [
  { id: "ten-cents", label: "$0.10", amount: 0.1, weight: 26 },
  { id: "twenty-cents", label: "$0.20", amount: 0.2, weight: 22 },
  { id: "thirty-cents", label: "$0.30", amount: 0.3, weight: 18 },
  { id: "bonus-20", label: "20% bonus", amount: 0, weight: 6 },
  { id: "better-luck", label: "Better luck", amount: 0, weight: 14 },
  { id: "bonus-30", label: "30% bonus", amount: 0, weight: 6 },
  { id: "one-dollar", label: "$1.00", amount: 1, weight: 8 },
  { id: "bonus-50", label: "50% bonus", amount: 0, weight: 6 },
];

const defaultPlayStreakRewards = [
  { days: 7, amount: 1 },
  { days: 14, amount: 2 },
  { days: 30, amount: 4 },
  { days: 60, amount: 7 },
  { days: 90, amount: 18 },
];

function allocateWholePercentages(values, targetTotal = 100) {
  const cleanValues = values.map((value) => Math.max(0, Number(value || 0)));
  const total = cleanValues.reduce((sum, value) => sum + value, 0);
  const source = total > 0 ? cleanValues : cleanValues.map(() => 1);
  const sourceTotal = source.reduce((sum, value) => sum + value, 0) || 1;
  const exact = source.map((value) => (value / sourceTotal) * targetTotal);
  const allocated = exact.map((value) => Math.floor(value));
  let remainder = targetTotal - allocated.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);
  for (let index = 0; index < remainder; index += 1) {
    allocated[order[index % order.length].index] += 1;
  }
  return allocated;
}

function normalizeSpinChances(rows) {
  const chances = allocateWholePercentages(rows.map((row) => row.weight), 100);
  return rows.map((row, index) => ({ ...row, weight: chances[index] }));
}

function rebalanceSpinChance(rows, changedIndex, value) {
  const chance = Math.max(0, Math.min(100, Math.round(Number(value || 0))));
  const otherRows = rows.filter((_, index) => index !== changedIndex);
  const otherChances = allocateWholePercentages(otherRows.map((row) => row.weight), 100 - chance);
  let otherIndex = 0;
  return rows.map((row, index) => {
    if (index === changedIndex) return { ...row, weight: chance };
    const next = { ...row, weight: otherChances[otherIndex] };
    otherIndex += 1;
    return next;
  });
}

const emptyAchievement = {
  title: "First deposit",
  icon: "trophy",
  description: "Make an approved deposit in this club.",
  goal: "signup",
  target_value: 1,
  reward_credits: 5,
  claims_per_player: 1,
  require_phone: false,
  active: true,
};

const emptyReward = {
  rank: 1,
  title: "Weekly champion",
  description: "Highest paid depositor this week.",
  reward_type: "freeplay",
  reward_amount: 25,
  active: true,
};

const goalOptions = [
  ["signup", "Signup"],
  ["deposit_count", "Deposit count"],
  ["deposit_total", "Deposit total"],
  ["game_load_count", "Game load count"],
  ["game_load_total", "Game load total"],
  ["referrals_count", "Referrals"],
  ["referral_deposit_count", "Referral deposits"],
];

const inputClass = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-[#F2A100]";
const labelClass = "mb-2 block text-sm font-semibold text-white";

function Toggle({ checked, onChange, label, gold = false }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-label={label}
      className={`relative h-8 w-14 rounded-full transition-colors ${checked ? (gold ? "bg-[#F2C230]" : "bg-[#2F6FA5]") : "bg-white/10"}`}
    >
      <span className={`absolute top-1 h-6 w-6 rounded-full bg-black transition-transform ${checked ? "translate-x-7" : "translate-x-1"}`} />
    </button>
  );
}

export default function AdminRewards() {
  const [settings, setSettings] = useState(defaultSettings);
  const [achievementRules, setAchievementRules] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [dailySpinRewards, setDailySpinRewards] = useState(defaultDailySpinRewards);
  const [playStreakRewards, setPlayStreakRewards] = useState(defaultPlayStreakRewards);
  const [preview, setPreview] = useState([]);
  const [weekStart, setWeekStart] = useState("");
  const [achievementForm, setAchievementForm] = useState(emptyAchievement);
  const [form, setForm] = useState(emptyReward);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingAchievement, setSavingAchievement] = useState(false);
  const [savingAchievementId, setSavingAchievementId] = useState("");
  const [savingReward, setSavingReward] = useState(false);

  const syncPayload = useCallback((data) => {
    setSettings({ ...defaultSettings, ...(data.settings || {}) });
    setAchievementRules(data.achievement_rules || []);
    setRewards(data.leaderboard_rewards || []);
    setDailySpinRewards(normalizeSpinChances(data.daily_spin_rewards?.length ? data.daily_spin_rewards : defaultDailySpinRewards));
    setPlayStreakRewards(data.play_streak_rewards?.length ? data.play_streak_rewards : defaultPlayStreakRewards);
    setPreview(data.preview || []);
    setWeekStart(data.week_start || "");
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/rewards");
    syncPayload(data);
  }, [syncPayload]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const activeRewards = useMemo(() => rewards.filter((row) => row.active), [rewards]);
  const activeAchievements = useMemo(() => achievementRules.filter((row) => row.active), [achievementRules]);

  const updateSettings = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateAchievementForm = (key, value) => {
    setAchievementForm((current) => ({ ...current, [key]: value }));
  };

  const updateAchievementRule = (id, key, value) => {
    setAchievementRules((rows) => rows.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  const updateDailySpinReward = (index, key, value) => {
    setDailySpinRewards((rows) => (
      key === "weight"
        ? rebalanceSpinChance(rows, index, value)
        : rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row))
    ));
  };
  const updatePlayStreakReward = (index, key, value) => {
    setPlayStreakRewards((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        achievements_enabled: Boolean(settings.achievements_enabled),
        achievements_locked_message: String(settings.achievements_locked_message || "").slice(0, 300),
        leaderboard_rewards_enabled: Boolean(settings.leaderboard_rewards_enabled),
        leaderboard_locked_message: String(settings.leaderboard_locked_message || "").slice(0, 300),
        daily_spin_enabled: Boolean(settings.daily_spin_enabled),
        daily_spin_locked_message: String(settings.daily_spin_locked_message || "").slice(0, 300),
        play_streak_enabled: Boolean(settings.play_streak_enabled),
        play_streak_locked_message: String(settings.play_streak_locked_message || "").slice(0, 300),
        daily_spin_rewards: dailySpinRewards.map((row) => ({
          id: row.id,
          label: String(row.label || "").trim(),
          amount: Number(row.amount || 0),
          weight: Number(row.weight || 0),
        })),
        play_streak_rewards: playStreakRewards.map((row) => ({
          days: Number(row.days || 0),
          amount: Number(row.amount || 0),
        })),
      };
      const { data } = await api.put("/admin/rewards/settings", payload);
      syncPayload(data);
      toast.success("Reward settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveNewAchievement = async (event) => {
    event.preventDefault();
    const payload = achievementPayload(achievementForm);
    if (!payload.title) {
      toast.error("Achievement title is required");
      return;
    }
    setSavingAchievement(true);
    try {
      const { data } = await api.post("/admin/rewards/achievements", payload);
      syncPayload(data);
      setAchievementForm(emptyAchievement);
      toast.success("Achievement rule added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAchievement(false);
    }
  };

  const saveAchievementRule = async (row) => {
    const payload = achievementPayload(row);
    if (!payload.title) {
      toast.error("Achievement title is required");
      return;
    }
    setSavingAchievementId(row.id);
    try {
      const { data } = await api.put(`/admin/rewards/achievements/${row.id}`, payload);
      syncPayload(data);
      toast.success("Achievement rule saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAchievementId("");
    }
  };

  const toggleAchievementRule = async (row) => {
    await saveAchievementRule({ ...row, active: !row.active });
  };

  const deleteAchievementRule = async (row) => {
    if (!window.confirm(`Delete ${row.title}?`)) return;
    setSavingAchievementId(row.id);
    try {
      const { data } = await api.delete(`/admin/rewards/achievements/${row.id}`);
      syncPayload(data);
      toast.success("Achievement rule deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAchievementId("");
    }
  };

  const saveReward = async (event) => {
    event.preventDefault();
    const payload = {
      rank: Number(form.rank || 1),
      title: String(form.title || "").trim(),
      description: String(form.description || "").trim(),
      reward_type: form.reward_type || "freeplay",
      reward_amount: Number(form.reward_amount || 0),
      active: Boolean(form.active),
    };
    if (!payload.title) {
      toast.error("Reward title is required");
      return;
    }
    setSavingReward(true);
    try {
      const request = editingId
        ? api.put(`/admin/rewards/leaderboard/${editingId}`, payload)
        : api.post("/admin/rewards/leaderboard", payload);
      const { data } = await request;
      syncPayload(data);
      setForm(emptyReward);
      setEditingId("");
      toast.success(editingId ? "Reward updated" : "Reward added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingReward(false);
    }
  };

  const editReward = (row) => {
    setEditingId(row.id);
    setForm({
      rank: row.rank || 1,
      title: row.title || "",
      description: row.description || "",
      reward_type: row.reward_type || "freeplay",
      reward_amount: row.reward_amount || 0,
      active: row.active !== false,
    });
  };

  const deleteReward = async (row) => {
    if (!window.confirm(`Delete Top ${row.rank} reward?`)) return;
    try {
      const { data } = await api.delete(`/admin/rewards/leaderboard/${row.id}`);
      syncPayload(data);
      if (editingId === row.id) {
        setEditingId("");
        setForm(emptyReward);
      }
      toast.success("Reward deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelEdit = () => {
    setEditingId("");
    setForm(emptyReward);
  };

  return (
    <div className="mx-auto w-full max-w-7xl p-3 text-slate-200 sm:p-6 md:p-8">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="h-6 w-6 text-[#F2C230]" />
          <h1 className="text-2xl font-bold tracking-tight text-white">Rewards</h1>
        </div>
        <p className="mt-1 max-w-4xl text-sm text-slate-400">
          Control client-facing achievement and leaderboard rewards for this site. Existing leaderboard rewards are still available below.
        </p>
      </header>

      <form onSubmit={saveSettings} className="mb-6 rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
        <div className="grid gap-5 lg:grid-cols-2">
          <RewardSettingCard
            title="Achievements"
            description="Disabled clients see a locked card."
            enabled={settings.achievements_enabled}
            onToggle={(value) => updateSettings("achievements_enabled", value)}
            message={settings.achievements_locked_message}
            onMessage={(value) => updateSettings("achievements_locked_message", value)}
            placeholder="Achievement rewards are paused right now."
          />
          <RewardSettingCard
            title="Leaderboards"
            description="Disabled claims return a locked state."
            enabled={settings.leaderboard_rewards_enabled}
            onToggle={(value) => updateSettings("leaderboard_rewards_enabled", value)}
            message={settings.leaderboard_locked_message}
            onMessage={(value) => updateSettings("leaderboard_locked_message", value)}
            placeholder="Leaderboard rewards are paused this week."
          />
          <DailySpinSettingCard
            rewards={dailySpinRewards}
            enabled={settings.daily_spin_enabled}
            message={settings.daily_spin_locked_message}
            onToggle={(value) => updateSettings("daily_spin_enabled", value)}
            onMessage={(value) => updateSettings("daily_spin_locked_message", value)}
            onRewardChange={updateDailySpinReward}
          />
          <PlayStreakSettingCard
            rewards={playStreakRewards}
            enabled={settings.play_streak_enabled}
            message={settings.play_streak_locked_message}
            onToggle={(value) => updateSettings("play_streak_enabled", value)}
            onMessage={(value) => updateSettings("play_streak_locked_message", value)}
            onRewardChange={updatePlayStreakReward}
          />
        </div>
        <button
          disabled={savingSettings}
          className="mt-5 inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {savingSettings ? "Saving..." : "Save reward settings"}
        </button>
      </form>

      <section className="mb-6 rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">Add achievement</h2>
            <p className="mt-1 text-sm text-slate-400">Goal, reward, repeat, and phone requirement.</p>
          </div>
          <span className="rounded-full border border-[#F2C230]/30 px-3 py-1 text-xs font-black text-[#F2C230]">Vendor scoped</span>
        </div>

        <form onSubmit={saveNewAchievement} className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className={labelClass}>Title</span>
              <input value={achievementForm.title} onChange={(event) => updateAchievementForm("title", event.target.value)} className={inputClass} maxLength={80} required />
            </label>
            <label className="block">
              <span className={labelClass}>Icon</span>
              <input value={achievementForm.icon} onChange={(event) => updateAchievementForm("icon", event.target.value)} className={inputClass} maxLength={40} placeholder="trophy" />
            </label>
          </div>

          <label className="block">
            <span className={labelClass}>Description</span>
            <textarea
              value={achievementForm.description}
              maxLength={240}
              onChange={(event) => updateAchievementForm("description", event.target.value)}
              className={`${inputClass} min-h-20 resize-y`}
              placeholder="Make an approved deposit in this club."
            />
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className={labelClass}>Goal</span>
              <select value={achievementForm.goal} onChange={(event) => updateAchievementForm("goal", event.target.value)} className={inputClass}>
                {goalOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={labelClass}>Target value</span>
              <input type="number" min="0" step="0.01" value={achievementForm.target_value} onChange={(event) => updateAchievementForm("target_value", event.target.value)} className={inputClass} />
            </label>
            <label className="block">
              <span className={labelClass}>Reward credits</span>
              <input type="number" min="0" step="0.01" value={achievementForm.reward_credits} onChange={(event) => updateAchievementForm("reward_credits", event.target.value)} className={inputClass} />
            </label>
            <label className="block">
              <span className={labelClass}>Claims per player</span>
              <input type="number" min="1" step="1" value={achievementForm.claims_per_player} onChange={(event) => updateAchievementForm("claims_per_player", event.target.value)} className={inputClass} />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-5">
            <CheckToggle checked={achievementForm.require_phone} onChange={(value) => updateAchievementForm("require_phone", value)} label="Require phone" />
            <CheckToggle checked={achievementForm.active} onChange={(value) => updateAchievementForm("active", value)} label="Active" />
            <button
              disabled={savingAchievement}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {savingAchievement ? "Saving..." : "Add rule"}
            </button>
          </div>
        </form>
      </section>

      <section className="mb-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">Achievement rules</h2>
            <p className="mt-1 text-sm text-slate-400">{activeAchievements.length} active rule{activeAchievements.length === 1 ? "" : "s"}.</p>
          </div>
        </div>

        {achievementRules.map((row) => (
          <AchievementRuleCard
            key={row.id}
            row={row}
            saving={savingAchievementId === row.id}
            onChange={updateAchievementRule}
            onSave={saveAchievementRule}
            onToggle={toggleAchievementRule}
            onDelete={deleteAchievementRule}
          />
        ))}

        {!loading && achievementRules.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 bg-[#101010] p-8 text-center text-sm text-slate-500">
            No achievement rules configured yet.
          </div>
        )}
      </section>

      <section className="mb-6 grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <form onSubmit={saveReward} className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">{editingId ? "Edit leaderboard reward" : "Add leaderboard reward"}</h2>
              <p className="mt-1 text-sm text-slate-400">Existing weekly leaderboard reward editor.</p>
            </div>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5">
                Cancel edit
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className={labelClass}>Rank</span>
              <select value={form.rank} onChange={(event) => updateForm("rank", event.target.value)} className={inputClass}>
                {Array.from({ length: 10 }, (_, index) => index + 1).map((rank) => (
                  <option key={rank} value={rank}>Top {rank}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className={labelClass}>Reward type</span>
              <select value={form.reward_type} onChange={(event) => updateForm("reward_type", event.target.value)} className={inputClass}>
                <option value="freeplay">Freeplay</option>
                <option value="wallet">Wallet bonus</option>
                <option value="custom">Custom / manual</option>
              </select>
            </label>

            <label className="block">
              <span className={labelClass}>Title</span>
              <input value={form.title} onChange={(event) => updateForm("title", event.target.value)} className={inputClass} maxLength={80} required />
            </label>

            <label className="block">
              <span className={labelClass}>Reward amount ($)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.reward_amount}
                onChange={(event) => updateForm("reward_amount", event.target.value)}
                className={inputClass}
              />
            </label>
          </div>

          <label className="mt-4 block">
            <span className={labelClass}>Description</span>
            <textarea
              value={form.description}
              maxLength={240}
              onChange={(event) => updateForm("description", event.target.value)}
              className={`${inputClass} min-h-24 resize-y`}
              placeholder="Highest paid depositor this week gets this bonus."
            />
          </label>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <CheckToggle checked={form.active} onChange={(value) => updateForm("active", value)} label="Active" />
            <button
              disabled={savingReward}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {savingReward ? "Saving..." : editingId ? "Save reward" : "Add reward"}
            </button>
          </div>
        </form>

        <section className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#2F6FA5]" />
            <h2 className="text-xl font-bold text-white">This week preview</h2>
          </div>
          <p className="mb-4 text-xs text-slate-500">Week starts {formatDate(weekStart)}.</p>
          <div className="space-y-3">
            {preview.slice(0, 5).map((row) => (
              <div key={`${row.username}-${row.rank}`} className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl font-black text-[#2F6FA5]">#{row.rank}</span>
                      <VerifiedName className="font-bold text-white" name={row.username} user={row} />
                    </div>
                    <p className="mt-1 text-sm text-slate-400">{moneyFromUnits(row.weekly_load_coins)} paid deposits this week</p>
                  </div>
                  {row.reward && (
                    <span className="shrink-0 rounded-full border border-[#2F6FA5]/40 bg-[#2F6FA5]/10 px-3 py-1 text-xs font-black text-[#B7D7F2]">
                      {row.reward.label}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!loading && preview.length === 0 && (
              <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
                No paid depositors this week yet.
              </div>
            )}
          </div>
        </section>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#3A3216]/80 bg-[#101010]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4 sm:p-5">
          <div>
            <h2 className="text-lg font-bold text-white">Leaderboard rewards</h2>
            <p className="mt-1 text-sm text-slate-400">{activeRewards.length} active reward{activeRewards.length === 1 ? "" : "s"}.</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
              <tr className="border-b border-white/5">
                <th className="px-4 py-4">Rank</th>
                <th className="px-4 py-4">Reward</th>
                <th className="px-4 py-4">Type</th>
                <th className="px-4 py-4 text-right">Amount</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-4 py-4 font-display text-xl font-black text-[#2F6FA5]">Top {row.rank}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-white">{row.title}</div>
                    <div className="max-w-xl text-xs text-slate-500">{row.description || "-"}</div>
                  </td>
                  <td className="px-4 py-4 capitalize text-slate-300">{rewardTypeLabel(row.reward_type)}</td>
                  <td className="px-4 py-4 text-right font-mono text-[#B7D7F2]">{money(row.reward_amount)}</td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${row.active ? "bg-emerald-500/10 text-emerald-300" : "bg-white/10 text-slate-400"}`}>
                      {row.active ? "Active" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => editReward(row)} className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button onClick={() => deleteReward(row)} className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rewards.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">No rewards configured yet.</td>
                </tr>
              )}
              {loading && rewards.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">Loading rewards...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function RewardSettingCard({ title, description, enabled, onToggle, message, onMessage, placeholder }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <p className="mt-1 text-sm text-slate-400">{description}</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label={`Toggle ${title}`} gold />
      </div>
      <textarea
        value={message}
        maxLength={300}
        onChange={(event) => onMessage(event.target.value)}
        className={`${inputClass} min-h-24 resize-y`}
        placeholder={placeholder}
      />
    </div>
  );
}

function DailySpinSettingCard({ enabled, message, onMessage, onRewardChange, onToggle, rewards }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <RotateCw className="h-4 w-4 text-[#F2C230]" />
            <h2 className="text-lg font-bold text-white">Daily Spin</h2>
          </div>
          <p className="mt-1 text-sm text-slate-400">Edit each prize chance directly. Other chances adjust automatically so the total stays 100%.</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label="Toggle daily spin" gold />
      </div>
      <textarea
        value={message}
        maxLength={300}
        onChange={(event) => onMessage(event.target.value)}
        className={`${inputClass} mb-4 min-h-20 resize-y`}
        placeholder="Daily spin is paused right now."
      />
      <div className="space-y-3">
        {rewards.map((row, index) => {
          return (
            <div key={row.id || index} className="grid gap-3 rounded-xl border border-white/10 bg-black/20 p-3 md:grid-cols-[1fr_0.7fr_0.7fr]">
              <label className="block">
                <span className={labelClass}>Label</span>
                <input value={row.label || ""} onChange={(event) => onRewardChange(index, "label", event.target.value)} className={inputClass} maxLength={32} />
              </label>
              <label className="block">
                <span className={labelClass}>Freeplay $</span>
                <input type="number" min="0" step="0.01" value={row.amount} onChange={(event) => onRewardChange(index, "amount", event.target.value)} className={inputClass} />
              </label>
              <label className="block">
                <span className={labelClass}>Chance %</span>
                <input type="number" min="0" max="100" step="1" value={row.weight} onChange={(event) => onRewardChange(index, "weight", event.target.value)} className={inputClass} />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlayStreakSettingCard({ enabled, message, onMessage, onRewardChange, onToggle, rewards }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Flame className="h-4 w-4 text-emerald-300" />
            <h2 className="text-lg font-bold text-white">Play Streak</h2>
          </div>
          <p className="mt-1 text-sm text-slate-400">Control streak milestone days and Freeplay amount.</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label="Toggle play streak" gold />
      </div>
      <textarea
        value={message}
        maxLength={300}
        onChange={(event) => onMessage(event.target.value)}
        className={`${inputClass} mb-4 min-h-20 resize-y`}
        placeholder="Play streak rewards are paused right now."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {rewards.map((row, index) => (
          <div key={`${row.days}-${index}`} className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-black/20 p-3">
            <label className="block">
              <span className={labelClass}>Days</span>
              <input type="number" min="1" step="1" value={row.days} onChange={(event) => onRewardChange(index, "days", event.target.value)} className={inputClass} />
            </label>
            <label className="block">
              <span className={labelClass}>Freeplay $</span>
              <input type="number" min="0" step="0.01" value={row.amount} onChange={(event) => onRewardChange(index, "amount", event.target.value)} className={inputClass} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function AchievementRuleCard({ row, saving, onChange, onSave, onToggle, onDelete }) {
  return (
    <div className="rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-white">{row.title || "Achievement"}</h3>
          <p className="mt-1 text-sm text-slate-400">{goalLabel(row.goal)} / target {formatGoalTarget(row.target_value, row.goal)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-4 py-1.5 text-xs font-black ${row.active ? "bg-[#F2C230] text-black" : "border border-white/10 text-slate-400"}`}>
            {row.active ? "Active" : "Paused"}
          </span>
          <button
            type="button"
            onClick={() => onToggle(row)}
            disabled={saving}
            className="rounded-xl border border-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/5 disabled:opacity-60"
          >
            {row.active ? "Pause" : "Activate"}
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_0.5fr_0.5fr]">
        <label className="block">
          <span className={labelClass}>Title</span>
          <input value={row.title || ""} onChange={(event) => onChange(row.id, "title", event.target.value)} className={inputClass} maxLength={80} />
        </label>
        <label className="block">
          <span className={labelClass}>Goal</span>
          <select value={row.goal || "signup"} onChange={(event) => onChange(row.id, "goal", event.target.value)} className={inputClass}>
            {goalOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={labelClass}>Target</span>
          <input type="number" min="0" step="0.01" value={row.target_value ?? 1} onChange={(event) => onChange(row.id, "target_value", event.target.value)} className={inputClass} />
        </label>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[0.5fr_0.5fr_1fr]">
        <label className="block">
          <span className={labelClass}>Reward</span>
          <input type="number" min="0" step="0.01" value={row.reward_credits ?? 0} onChange={(event) => onChange(row.id, "reward_credits", event.target.value)} className={inputClass} />
        </label>
        <label className="block">
          <span className={labelClass}>Claims</span>
          <input type="number" min="1" step="1" value={row.claims_per_player ?? 1} onChange={(event) => onChange(row.id, "claims_per_player", event.target.value)} className={inputClass} />
        </label>
        <label className="block">
          <span className={labelClass}>Description</span>
          <input value={row.description || ""} onChange={(event) => onChange(row.id, "description", event.target.value)} className={inputClass} maxLength={240} />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <CheckToggle checked={row.require_phone} onChange={(value) => onChange(row.id, "require_phone", value)} label="Require phone" />
        <button
          type="button"
          onClick={() => onSave(row)}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-5 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving..." : "Save rule"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(row)}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-500/30 px-5 py-3 text-sm font-bold text-red-300 hover:bg-red-500/10 disabled:opacity-60"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </button>
      </div>
    </div>
  );
}

function CheckToggle({ checked, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="inline-flex items-center gap-2 text-sm font-bold text-white">
      {checked ? (
        <CheckCircle2 className="h-5 w-5 rounded-full text-[#F2C230]" />
      ) : (
        <span className="h-5 w-5 rounded-full border border-[#F2C230]" />
      )}
      {label}
    </button>
  );
}

function achievementPayload(row) {
  return {
    title: String(row.title || "").trim(),
    icon: String(row.icon || "trophy").trim(),
    description: String(row.description || "").trim(),
    goal: row.goal || "signup",
    target_value: Number(row.target_value || 0),
    reward_credits: Number(row.reward_credits || 0),
    claims_per_player: Number(row.claims_per_player || 1),
    require_phone: Boolean(row.require_phone),
    active: Boolean(row.active),
  };
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function moneyFromUnits(value) {
  return `$${((Number(value) || 0) / 1000).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function rewardTypeLabel(value) {
  if (value === "wallet") return "Wallet bonus";
  if (value === "custom") return "Custom";
  return "Freeplay";
}

function goalLabel(value) {
  const match = goalOptions.find(([key]) => key === value);
  return match ? match[1] : "Signup";
}

function formatGoalTarget(value, goal) {
  const number = Number(value || 0);
  if (["deposit_total", "game_load_total"].includes(goal)) {
    return money(number);
  }
  return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/\.?0+$/, "");
}
